import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, Calendar, Briefcase, BarChart3, Eye, CreditCard, Euro, TrendingUp, Target, FileSignature } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const STATS = [
  { key: "leads", icon: Users, label: "Leads", color: "#06B6D4", value: (s) => s.leads.total, sub: (s) => `${s.leads.nouveau} nouveaux · ${s.leads.gagne} gagnés` },
  { key: "rdv", icon: Calendar, label: "RDV", color: "#0A1128", value: (s) => s.appointments.total, sub: (s) => `${s.appointments.pending} en attente` },
  { key: "projets", icon: Briefcase, label: "Projets", color: "#FFDCA8", value: (s) => s.projects.total, sub: () => "actifs en pipeline" },
  { key: "visites", icon: Eye, label: "Visites", color: "#A7F3D0", value: (s) => s.visits.total, sub: (s) => `${s.visits.last14} sur 14j` },
  { key: "paye", icon: CreditCard, label: "Paiements", color: "#67E8F9", value: (s) => s.payments.paid, sub: () => "RDV visio confirmés" },
];

const fmtEur = (n) => Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [perf, setPerf] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/dashboard/performance").then((r) => setPerf(r.data)).catch(() => {});
  }, []);

  // Build combined monthly series: signed current + signed previous + paid current
  const series = perf
    ? perf.monthly_current.map((m, i) => ({
        month: ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"][i],
        [`signe_${perf.year}`]: m.amount,
        [`signe_${perf.year - 1}`]: perf.monthly_previous[i]?.amount || 0,
        [`paye_${perf.year}`]: (perf.paid && perf.paid.monthly_current && perf.paid.monthly_current[i]?.amount) || 0,
      }))
    : [];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Tableau de bord</h1>
      <p className="text-sm text-[#64748B] mt-1">Performance commerciale + activité globale.</p>

      {!perf && !stats && <div className="mt-8 text-[#64748B]">Chargement...</div>}

      {/* Performance KPIs */}
      {perf && (
        <div className="mt-3">
          <h2 className="display font-bold text-[#0A1128] text-lg mb-3 flex items-center gap-2">
            <TrendingUp size={18} className="text-[#06B6D4]" /> Performance commerciale {perf.year}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <KpiCard
              testId="kpi-ca-ytd"
              icon={Euro}
              label={`CA signé ${perf.year}`}
              value={fmtEur(perf.revenue.ytd)}
              sub={
                perf.revenue.yoy_delta_pct != null
                  ? <DeltaPill delta={perf.revenue.yoy_delta_pct} prev={`${perf.year - 1}: ${fmtEur(perf.revenue.prev_year)}`} />
                  : `vs ${perf.year - 1} : ${fmtEur(perf.revenue.prev_year)}`
              }
              color="#06B6D4"
            />
            <KpiCard
              testId="kpi-ca-paid-ytd"
              icon={CreditCard}
              label={`CA payé ${perf.year}`}
              value={fmtEur(perf.paid?.ytd || 0)}
              sub={`Ce mois : ${fmtEur(perf.paid?.month || 0)} · ${perf.year - 1} : ${fmtEur(perf.paid?.prev_year || 0)}`}
              color="#A7F3D0"
            />
            <KpiCard
              testId="kpi-ca-month"
              icon={Euro}
              label="CA signé ce mois"
              value={fmtEur(perf.revenue.month)}
              sub={`Même mois ${perf.year - 1} : ${fmtEur(perf.revenue.prev_year_same_month)}`}
              color="#FFDCA8"
            />
            <KpiCard
              testId="kpi-pipeline"
              icon={Target}
              label={`Pipeline pondéré (${perf.pipeline.count})`}
              value={fmtEur(perf.pipeline.weighted)}
              sub={`Total potentiel : ${fmtEur(perf.pipeline.total)}`}
              color="#A7F3D0"
            />
            <KpiCard
              testId="kpi-conversion"
              icon={FileSignature}
              label="Taux de conversion"
              value={`${perf.conversion_rate_pct}%`}
              sub={`${perf.signed_count} signés / ${perf.sent_count} envoyés`}
              color="#67E8F9"
            />
            <KpiCard
              testId="kpi-pipeline-count"
              icon={Briefcase}
              label="Opportunités en cours"
              value={String(perf.pipeline.count)}
              sub="Brouillon + envoyé + négociation"
              color="#94a3b8"
            />
            <KpiCard
              testId="kpi-status"
              icon={BarChart3}
              label="Répartition"
              value={String(Object.keys(perf.status_counts).length)}
              sub={
                <div className="flex flex-wrap gap-1 mt-1">
                  {Object.entries(perf.status_counts).slice(0, 4).map(([k, v]) => (
                    <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FAFAFA] border border-black/8 font-bold text-[#0A1128]">{k}: {v}</span>
                  ))}
                </div>
              }
              color="#0A1128"
            />
          </div>

          {/* Revenue trend chart — signé vs payé */}
          <div className="mt-4 bg-white border border-black/8 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Euro size={16} className="text-[#06B6D4]" />
                <h3 className="display font-bold text-[#0A1128]">CA mensuel — Signé vs Payé · {perf.year}</h3>
              </div>
              <div className="text-[10px] text-[#64748B] flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#06B6D4]" /> Signé (devis)</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#A7F3D0]" /> Payé (factures)</span>
              </div>
            </div>
            <div className="h-64 w-full" data-testid="perf-monthly-chart">
              <ResponsiveContainer>
                <LineChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k€` : `${v}€`} />
                  <Tooltip formatter={(v) => fmtEur(v)} />
                  <Legend />
                  <Line type="monotone" name={`Signé ${perf.year}`} dataKey={`signe_${perf.year}`} stroke="#06B6D4" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" name={`Payé ${perf.year}`} dataKey={`paye_${perf.year}`} stroke="#A7F3D0" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" name={`Signé ${perf.year - 1}`} dataKey={`signe_${perf.year - 1}`} stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Daily CA — last 30 days */}
          {perf.daily_30d && perf.daily_30d.length > 0 && (
            <div className="mt-4 bg-white border border-black/8 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className="text-[#06B6D4]" />
                  <h3 className="display font-bold text-[#0A1128]">CA quotidien — 30 derniers jours</h3>
                </div>
                <div className="text-[10px] text-[#64748B] flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#06B6D4]" /> Signé</span>
                  <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#A7F3D0]" /> Payé</span>
                </div>
              </div>
              <div className="h-56 w-full" data-testid="perf-daily-chart">
                <ResponsiveContainer>
                  <LineChart data={perf.daily_30d}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={4} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k€` : `${v}€`} />
                    <Tooltip formatter={(v) => fmtEur(v)} labelFormatter={(label, payload) => payload?.[0]?.payload?.date || label} />
                    <Legend />
                    <Line type="monotone" name="Signé" dataKey="signed" stroke="#06B6D4" strokeWidth={2.5} dot={{ r: 2 }} />
                    <Line type="monotone" name="Payé" dataKey="paid" stroke="#A7F3D0" strokeWidth={2.5} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Original activity stats */}
      {stats && (
        <div className="mt-4">
          <h2 className="display font-bold text-[#0A1128] text-lg mb-3 flex items-center gap-2">
            <Eye size={18} className="text-[#06B6D4]" /> Activité du site
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {STATS.map((s) => (
              <div key={s.key} data-testid={`stat-${s.key}`} className="bg-white border border-black/8 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <s.icon size={18} style={{ color: s.color }} />
                  <span className="text-[10px] tracking-widest uppercase text-[#64748B]">{s.label}</span>
                </div>
                <div className="display text-2xl sm:text-3xl font-extrabold text-[#0A1128] mt-2">{s.value(stats)}</div>
                <div className="text-[11px] text-[#64748B] mt-1">{s.sub(stats)}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-black/8 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 size={16} className="text-[#06B6D4]" />
                <h3 className="display font-bold text-[#0A1128]">Trafic — 14 derniers jours</h3>
              </div>
              <div className="h-64 w-full">
                <ResponsiveContainer>
                  <LineChart data={stats.traffic_14d}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="visits" stroke="#06B6D4" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-black/8 rounded-2xl p-5">
              <h3 className="display font-bold text-[#0A1128] mb-3">Pages les plus vues</h3>
              <ul className="space-y-2">
                {(stats.top_paths || []).map((p) => (
                  <li key={p.path} className="flex items-center justify-between bg-[#FAFAFA] rounded-lg px-3 py-2">
                    <span className="text-sm text-[#0A1128] truncate">{p.path || "/"}</span>
                    <span className="text-xs font-bold text-[#06B6D4]">{p.count}</span>
                  </li>
                ))}
                {(!stats.top_paths || stats.top_paths.length === 0) && (
                  <li className="text-sm text-[#64748B]">Pas encore de données.</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ testId, icon: Icon, label, value, sub, color }) {
  return (
    <div data-testid={testId} className="bg-white border border-black/8 rounded-2xl p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <Icon size={18} style={{ color }} />
        <span className="text-[10px] tracking-widest uppercase text-[#64748B]">{label}</span>
      </div>
      <div className="display text-2xl sm:text-3xl font-extrabold text-[#0A1128] mt-2 tabular-nums">{value}</div>
      <div className="text-[11px] text-[#64748B] mt-1">{sub}</div>
    </div>
  );
}

function DeltaPill({ delta, prev }) {
  const positive = delta >= 0;
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${positive ? "bg-[#A7F3D0] text-[#065F46]" : "bg-red-100 text-red-600"}`}>
        {positive ? "▲" : "▼"} {Math.abs(delta)}%
      </span>
      <span className="text-[10px] text-[#64748B]">{prev}</span>
    </span>
  );
}
